import { LoadBasketStatus, LoadRead } from "src/store/api/generatedApi";
import { BasketStatusChipProps } from "./BasketStatusChip";

export const refreshMS = 5000;

// NOTE: These are client friendly names for the basket statuses.
// In the backend, we use the LoadBasketStatus enum.
export enum BasketStatus {
  Filled,
  Assigned,
  Unassigned,
}

/**
 * Represents information about a basket in the system
 * This is a client-side representation transformed from the API's LoadBasketRead type
 * Used to normalize basket data for display in UI components
 * assignment details (crane assigned to this basket), and a derived client-friendly
 * status representation (BasketStatus enum)
 */
export interface BasketInfo {
  loadBasketId: number;
  basketNumber: number;
  // backend status, e.g. "filled", "new", "partially_filled"
  status: LoadBasketStatus;
  assignedTo: {
    name: string;
    uid: string;
  } | null;
  // clientStatus is a derived status from the backend status
  // Converts the backend status to a client-friendly status
  // e.g. "filled" -> BasketStatus.Filled
  // e.g. "new" -> BasketStatus.Unassigned
  // e.g. "partially_filled" -> BasketStatus.Assigned
  clientStatus: BasketStatus;
}

/**
 * Computes the basket status based on its filled state and assignment status
 */
export const getBasketClientStatus = (
  basket: Omit<BasketInfo, "clientStatus">
): BasketStatus => {
  switch (basket.status) {
    case "filled":
      return BasketStatus.Filled;
    case "new":
      if (basket.assignedTo) {
        return BasketStatus.Assigned;
      } else {
        return BasketStatus.Unassigned;
      }
    // TODO: "partially_filled" is not used in the frontend, but it is in the
    // backend. Once, we start using it, we can add it to the switch statement.
    case "partially_filled":
      if (basket.assignedTo) {
        return BasketStatus.Assigned;
      } else {
        return BasketStatus.Unassigned;
      }
  }
};

/**
 * Creates props for BasketStatusChip component based on basket information
 */
export const getBasketStatusChipProps = (
  basket: BasketInfo
): BasketStatusChipProps => {
  switch (basket.clientStatus) {
    case BasketStatus.Filled:
      return { status: BasketStatus.Filled };
    case BasketStatus.Assigned:
      if (basket.assignedTo) {
        return {
          status: BasketStatus.Assigned,
          assignedName: basket.assignedTo.name,
          assignedUid: basket.assignedTo.uid,
        };
      } else {
        return { status: BasketStatus.Unassigned };
      }
    case BasketStatus.Unassigned:
      return { status: BasketStatus.Unassigned };
  }
};

export const isActive = (load: LoadRead) => {
  return (
    !isComplete(load) &&
    Object.values(load.baskets).some(
      (basket) => basket.assigned_to_uid !== null
    )
  );
};

export const isPending = (load: LoadRead) => {
  return (
    !isComplete(load) &&
    Object.values(load.baskets).every(
      (basket) => basket.assigned_to_uid === null
    )
  );
};

export const isComplete = (load: LoadRead) => {
  return Object.values(load.baskets).every(
    (basket) => basket.status == "filled"
  );
};

export const getLoadVariant = ({
  planId,
  currentPlanId,
  backupPlanId,
  nextPlanId,
}: {
  planId: number;
  currentPlanId: number | null;
  backupPlanId?: number | null;
  nextPlanId: number | null;
}): "new" | "backup" | "out_of_date" | "next" => {
  if (
    backupPlanId !== null &&
    backupPlanId !== undefined &&
    planId === backupPlanId
  ) {
    return "backup";
  } else if (nextPlanId !== null && planId === nextPlanId) {
    return "next";
  } else if (currentPlanId !== null && planId !== currentPlanId) {
    return "out_of_date";
  } else {
    return "new";
  }
};
